import { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { KeyRoundIcon, Trash2Icon } from "lucide-react";

import { useMe } from "@/api/me";
import { useUsers, type User } from "@/api/users";
import { DataTable, type ColumnDef } from "@/components/data-table/DataTable";
import { useDataTableState } from "@/components/data-table/useDataTableState";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatDate } from "@/utils/formatters";

import { DeleteUserDialog } from "./DeleteUserDialog";
import { ResetPasswordDialog } from "./ResetPasswordDialog";
import {
  TemporaryPasswordDialog,
  type Secret,
} from "./TemporaryPasswordDialog";
import { UserDialog } from "./UserDialog";

export default function UsersPage() {
  const { t } = useTranslation();
  const me = useMe();

  const ds = useDataTableState({
    defaultSort: { id: "email", desc: false },
    defaultPageSize: 50,
    filterDeps: [],
  });

  const query = useUsers({
    search: ds.debouncedSearch || undefined,
    ordering: ds.orderingParam,
  });

  const [editing, setEditing] = useState<User | null>(null);
  const [deleting, setDeleting] = useState<User | null>(null);
  const [resetting, setResetting] = useState<User | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [secret, setSecret] = useState<Secret | null>(null);

  const myId = me.data?.id;

  const columns = useMemo<ColumnDef<User>[]>(
    () => [
      {
        accessorKey: "email",
        header: t("settings.users.email"),
        enableSorting: true,
      },
      {
        // Shows the full name; the server can only order it by last name.
        id: "lastName",
        header: t("settings.users.columnName"),
        enableSorting: true,
        cell: ({ row }) =>
          [row.original.firstName, row.original.lastName]
            .filter(Boolean)
            .join(" ") || "–",
      },
      {
        id: "role",
        header: t("settings.users.role"),
        size: 100,
        cell: ({ row }) => (
          <Badge variant={row.original.isStaff ? "default" : "secondary"}>
            {row.original.isStaff
              ? t("settings.users.admin")
              : t("settings.users.user")}
          </Badge>
        ),
      },
      {
        accessorKey: "lastLogin",
        header: t("settings.users.lastLogin"),
        size: 160,
        enableSorting: true,
        cell: ({ row }) =>
          row.original.lastLogin ? formatDate(row.original.lastLogin) : "–",
      },
      {
        id: "actions",
        header: "",
        size: 96,
        cell: ({ row }) => (
          <div
            className="flex justify-end"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              variant="ghost"
              size="icon-sm"
              data-testid="reset-password"
              aria-label={t("settings.users.resetPassword")}
              onClick={() => setResetting(row.original)}
            >
              <KeyRoundIcon className="text-muted-foreground size-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              data-testid="delete"
              aria-label={t("common.delete")}
              disabled={row.original.id === myId}
              onClick={() => setDeleting(row.original)}
            >
              <Trash2Icon className="text-muted-foreground size-4" />
            </Button>
          </div>
        ),
      },
    ],
    [t, myId],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("settings.users.title")}
        meta={
          <p className="text-muted-foreground text-sm">
            {t("settings.users.description")}
          </p>
        }
        actions={
          <Button onClick={() => setNewOpen(true)}>
            {t("settings.users.create")}
          </Button>
        }
      />

      <DataTable<User>
        {...ds.tableProps}
        data={query.data ?? []}
        totalCount={query.data?.length ?? 0}
        isLoading={query.isLoading}
        columns={columns}
        searchPlaceholder={t("settings.users.searchPlaceholder")}
        emptyLabel={t("settings.users.empty")}
        getRowId={(r) => String(r.id)}
        onRowClick={(row) => setEditing(row)}
      />

      <UserDialog
        open={newOpen}
        user={editing}
        isSelf={editing != null && editing.id === myId}
        onClose={() => {
          setNewOpen(false);
          setEditing(null);
        }}
        onCreated={setSecret}
      />
      <DeleteUserDialog user={deleting} onClose={() => setDeleting(null)} />
      <ResetPasswordDialog
        user={resetting}
        onClose={() => setResetting(null)}
        onReset={setSecret}
      />
      <TemporaryPasswordDialog
        secret={secret}
        onClose={() => setSecret(null)}
      />
    </div>
  );
}
